import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../withTypes';
import { Status, STATUSES } from '@/types/store';
import { RootState } from '..';
import {
  TaskRatingData,
  TaskTuaaData,
  TaskWithData,
  UpdateTaskInput,
  UpdateTaskMutation,
  UpdateTaskMutationVariables,
  UpdateTaskRatingMutation,
  UpdateTaskRatingMutationVariables,
  UpdateTaskTuaaQuestionMutation,
  UpdateTaskTuaaQuestionMutationVariables,
  UpsertTaskNoteMutation,
  UpsertTaskNoteMutationVariables,
} from '@/graphql/types';
import {
  FETCH_MODEL_ASSESSMENT_TASKS,
  BOOKMARK_TASK_MUTATION,
  UPDATE_TASK,
  UPDATE_TASK_RATING,
  UPDATE_TASK_TUAA,
  UPSERT_TASK_NOTE,
} from '@/graphql/queries/tasks';
import { graphqlMutation, graphqlRequest, addRejectedToast, showRejectedToast } from '@/api/graphqlClient';
import { TASK_TYPES } from '@/types/assignments';
import { Note } from '@/types/assignmentsGeneral';
import { Paragraph } from '@/types/books';
import { BookmarkTask } from '@/types/tasks';
import { getNotes, showToast } from '@/utils/utils';
import { errorMessages } from '@/utils/errorMessages';
import i18n from '@i18n/i18n.config';

export interface RatingTask {
  id: number;
  taskTypeId: number;
  taskDataId: number;
  isBookmarked: boolean;
  isCompleted: boolean;
  title: string;
  sourceText: string;
  targetText: string;
  rating: number;
  notesList: Note[];
  bookId?: number;
}

export interface BookmarkModelAssessmentTask {
  taskId: number;
  isBookmarked: boolean;
}

export interface TuaaCarouselOption {
  value: string;
  label: string;
}

export interface TuaaCarouselQuestion {
  id: string;
  label: string;
  selectedOptionKey: null | string;
  options: TuaaCarouselOption[];
}

export interface TuaaTask {
  id: number;
  taskTypeId: number;
  taskDataId: number;
  isBookmarked: boolean;
  isCompleted: boolean;
  bookTitle: string;
  questions: TuaaCarouselQuestion[];
  paragraphs: Paragraph[];
  notesList: Note[];
  bookId?: number;
}

export interface ModelAssessment {
  allTaskList: (RatingTask | TuaaTask)[];
  selectedTaskId?: number | null;
  status: Status;
}

const initialState: ModelAssessment = {
  allTaskList: [],
  selectedTaskId: 0,
  status: STATUSES.IDLE,
};

interface UpsertNote {
  taskId: number;
  userId: number;
  noteText: string;
  noteId?: number | null;
}
export const fetchModelAssessmentTaskList = createAppAsyncThunk(
  'assignments/fetchModelAssessmentTaskList',
  async (assignmentId: number) => {
    const data = await graphqlRequest<{ assignmentId: number }, { tasksPerAssignment: TaskWithData[] }>(
      FETCH_MODEL_ASSESSMENT_TASKS,
      { assignmentId },
      'no-cache',
    );

    const filteredData = data.tasksPerAssignment
      .filter(
        (task: TaskWithData) =>
          task.data?.__typename === 'TaskRatingData'
          || task.data?.__typename === 'TaskTuaaData',
      );

    if (filteredData.length === 0) {
      showToast(errorMessages.taskNotFound, 'error');
    }

    return filteredData
      .map((task: TaskWithData) => {
        const taskData = task.data as TaskRatingData | TaskTuaaData;

        if (task.task_type_id === TASK_TYPES.RATE && 'source_sentence' in taskData) {
          return {
            id: task.id,
            taskDataId: task.task_data_id,
            title: `${i18n.t('bookList.' + taskData.source_sentence?.book_title)} ${taskData.source_sentence?.chapter_title} : ${taskData.source_sentence?.sentence_number}`,
            taskTypeId: task.task_type_id,
            assignmentId: task.assignment_id,
            isBookmarked: task.is_bookmarked,
            isCompleted: task.status_id === 4,
            sourceText: taskData.source_sentence?.sentence_text || '',
            targetText: taskData.translated_sentence?.sentence_text,
            rating: taskData.rating,
            notesList: getNotes(task.notes),
            bookId: taskData.source_sentence?.book_id,
          };
        }

        if (task.task_type_id === TASK_TYPES.TUAA && 'book_title' in taskData) {
          return {
            id: task.id,
            taskDataId: task.task_data_id,
            title: i18n.t('project.assignment.Survey'),
            taskTypeId: task.task_type_id,
            assignmentId: task.assignment_id,
            isBookmarked: task.is_bookmarked,
            isCompleted: task.status_id === 4,
            bookTitle: i18n.t(`project.assignment.Book of`, { bookTitle: i18n.t('bookList.' + taskData.book_title) || '' }),
            paragraphs: taskData.source_sentences.map(sourceSentence => ({
              id: sourceSentence.id,
              sourceText: sourceSentence.sentence_text,
              verseNumber: sourceSentence.sentence_number,
              translation: taskData.translated_sentences.find(sentence => sentence.sentence_number === sourceSentence.sentence_number)?.sentence_text,
            })),
            questions: taskData.questions.map(question => ({
              id: question.question_id.toString(),
              label: i18n.t(`project.model-assessment.tuaa-questions.${question.question}`),
              selectedOptionKey: question.answer?.toString(),
              options:
                question.question_type === 1
                  ? [
                    { value: '1', label: i18n.t('project.model-assessment.yes') },
                    { value: '2', label: i18n.t('project.model-assessment.no') },
                  ]
                  : [
                    { value: '1', label: i18n.t('project.model-assessment.yes') },
                    { value: '2', label: i18n.t('project.model-assessment.somewhat') },
                    { value: '3', label: i18n.t('project.model-assessment.no') },
                  ],
            })),
            notesList: getNotes(task.notes),
            bookId: undefined,
          };
        }
        return null;
      })
      .filter(Boolean);
  },
);

export const updateTask = createAppAsyncThunk(
  'tasks/updateTask',
  async ({ id, task }: { id: number, task: UpdateTaskInput }) => {
    const data = await graphqlMutation<UpdateTaskMutationVariables, UpdateTaskMutation>(
      UPDATE_TASK,
      { id, task },
    );
    return data.updateTask;
  },
);

export const bookmarkModelAssessmentTask = createAppAsyncThunk(
  'assignments/bookmarkModelAssessmentTask',
  async ({ taskId, isBookmarked }: { taskId: number, isBookmarked: boolean }) => {
    const result = await graphqlMutation<BookmarkModelAssessmentTask, { bookmarkTask: BookmarkTask }>(
      BOOKMARK_TASK_MUTATION,
      { taskId, isBookmarked },
    );

    return result.bookmarkTask;
  },
);

export const updateTaskRating = createAppAsyncThunk(
  'assignments/updateTaskRating',
  async ({ taskDataId, taskId, rating }: { taskDataId: number, rating: number, taskId: number }) => {
    const data = await graphqlMutation<UpdateTaskRatingMutationVariables, UpdateTaskRatingMutation>(
      UPDATE_TASK_RATING,
      { id: taskDataId, rating },
    );
    return { taskId, ...data.updateTaskRating };
  },
);

export const updateTaskTuaa = createAppAsyncThunk(
  'assignments/updateTaskTuaa',
  async ({ taskId, taskDataId, answer }: { taskId: number, taskDataId: number, answer: number }) => {
    const data = await graphqlMutation<UpdateTaskTuaaQuestionMutationVariables, UpdateTaskTuaaQuestionMutation>(
      UPDATE_TASK_TUAA,
      { id: taskDataId, answer },
    );
    return { taskId, taskDataId, ...data.updateTaskTuaaQuestion };
  },
);

export const upsertNote = createAppAsyncThunk(
  'modelAssessment/upsertNote',
  async ({ taskId, userId, noteText, noteId }: UpsertNote) => {
    const data = await graphqlMutation<UpsertTaskNoteMutationVariables, UpsertTaskNoteMutation>(
      UPSERT_TASK_NOTE,
      { taskId, userId, noteText, noteId },
    );
    return {
      data,
      taskId,
    };
  },
);

const incompleteTaskList = (taskList: (RatingTask | TuaaTask)[]) => {
  return taskList
    .filter(obj => obj.isCompleted === false);
};
const findNextIncompleteTask = (
  taskList: (RatingTask | TuaaTask)[],
  selectedTaskId: number,
): number | undefined => {
  const incompleteTasks = incompleteTaskList(taskList);
  const selectedIndex = incompleteTasks.findIndex(
    (obj: RatingTask | TuaaTask) => obj.id === selectedTaskId,
  );
  // check immediate next incomplete task
  if (selectedIndex >= 0 && incompleteTasks[selectedIndex + 1]) {
    return incompleteTasks[selectedIndex + 1].id;
  }
};

export const modelAssessmentReducer = createSlice({
  name: 'modelAssessment',
  initialState,
  reducers: {
    setSelectedTaskId: (state, action: PayloadAction<number>) => {
      state.selectedTaskId = action.payload;
    },
    selectNextTaskId: (state, action: PayloadAction<{ currentSelectedTaskId: number, taskListToUse: (RatingTask | TuaaTask)[] }>) => {
      const { currentSelectedTaskId, taskListToUse } = action.payload;
      const selectedIndex = taskListToUse.findIndex(task => task.id === currentSelectedTaskId);
      if (taskListToUse.length === 1) {
        state.selectedTaskId = taskListToUse[0].id;
        return;
      }
      if (selectedIndex === taskListToUse.length - 1) {
        state.selectedTaskId = taskListToUse[0].id;
        return;
      }
      if (selectedIndex >= 0 && taskListToUse[selectedIndex + 1]) {
        state.selectedTaskId = taskListToUse[selectedIndex + 1].id;
      }
    },
    submitAssignment: (
      state,
      action: PayloadAction<{ selectedTaskId: number, hideCompletedTasks: boolean }>,
    ) => {
      const { selectedTaskId } = action.payload;

      // Move to next incomplete task index
      state.selectedTaskId = findNextIncompleteTask(
        state.allTaskList,
        selectedTaskId,
      );
    },
    handleBookmark: (state, action: PayloadAction<number>) => {
      const currentTaskIndex = state.allTaskList.findIndex(
        (obj: RatingTask | TuaaTask) => obj.id === action.payload,
      );
      if (currentTaskIndex >= 0) {
        state.allTaskList[currentTaskIndex].isBookmarked = !state.allTaskList[currentTaskIndex].isBookmarked;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchModelAssessmentTaskList.pending, (state) => {
      state.status = STATUSES.PENDING;
    });
    builder.addCase(fetchModelAssessmentTaskList.fulfilled, (state, action) => {
      state.allTaskList = action.payload as (RatingTask | TuaaTask)[];
      state.selectedTaskId
        = (action.payload as (RatingTask | TuaaTask)[])
          .find((task: RatingTask | TuaaTask) => !task.isCompleted)?.id
          ?? (action.payload as (RatingTask | TuaaTask)[])[0]?.id
          ?? null;
      state.status = STATUSES.FULFILLED;
    });
    builder.addCase(fetchModelAssessmentTaskList.rejected, (state, action) => {
      state.allTaskList = initialState.allTaskList;
      state.selectedTaskId = initialState.selectedTaskId;
      state.status = STATUSES.REJECTED;
      showRejectedToast(action, errorMessages.fetchingTasks);
    });
    builder.addCase(bookmarkModelAssessmentTask.fulfilled, (state, action) => {
      if (action.payload) {
        const { id, is_bookmarked } = action.payload;
        const taskIndex = state.allTaskList.findIndex(task => task.id === id);
        if (taskIndex >= 0) {
          state.allTaskList[taskIndex].isBookmarked = is_bookmarked;
        }
      }
    });
    addRejectedToast(builder, bookmarkModelAssessmentTask, errorMessages.bookmarkingTask);
    builder.addCase(updateTask.fulfilled, (state, action: PayloadAction<{ id: number, status_id: number }>) => {
      const { id, status_id } = action.payload;
      const taskIndex = state.allTaskList.findIndex(existingTask => existingTask.id === id);
      if (taskIndex >= 0) {
        state.allTaskList[taskIndex] = {
          ...state.allTaskList[taskIndex],
          isCompleted: status_id === 4,
        };
      }
    });
    addRejectedToast(builder, updateTask, errorMessages.updatingTask);
    builder.addCase(updateTaskRating.fulfilled, (state, action) => {
      const { taskId, rating } = action.payload;
      const taskIndex = state.allTaskList.findIndex(existingTask => existingTask.id === taskId);
      if (taskIndex >= 0 && rating != null) {
        state.allTaskList[taskIndex] = {
          ...state.allTaskList[taskIndex],
          rating,
        };
      }
    });
    addRejectedToast(builder, updateTaskRating, errorMessages.updatingTask);
    builder.addCase(updateTaskTuaa.fulfilled, (state, action) => {
      const { taskId, tuaa_question_id: question_id, answer } = action.payload;
      const taskIndex = state.allTaskList.findIndex(existingTask => existingTask.id === taskId);

      if (taskIndex >= 0) {
        const existingTask = state.allTaskList[taskIndex];

        if ('questions' in existingTask && Array.isArray(existingTask.questions)) {
          state.allTaskList[taskIndex] = {
            ...existingTask,
            questions: existingTask.questions.map(question =>
              question.id === question_id.toString() ? { ...question, answer, selectedOptionKey: answer?.toString() ?? '' } : question,
            ),
          };
        }
      }
    });
    addRejectedToast(builder, updateTaskTuaa, errorMessages.updatingTask);
    builder.addCase(upsertNote.fulfilled, (state, action) => {
      const { taskId, data } = action.payload ?? {};
      const note = data?.upsertTaskNote;
      if (!note) return;

      const taskIndex = state.allTaskList.findIndex(task => task.id === taskId);
      const updatedTasks = [...state.allTaskList];
      const updatedNotes = [...updatedTasks[taskIndex].notesList];

      const noteIndex = updatedNotes.findIndex(taskNote => taskNote.id === note.id);
      if (noteIndex < 0) {
        updatedNotes.push(note);
      } else {
        updatedNotes[noteIndex] = note;
      }

      updatedTasks[taskIndex] = {
        ...updatedTasks[taskIndex],
        notesList: updatedNotes,
      };

      state.allTaskList = updatedTasks;
    });
    addRejectedToast(builder, upsertNote, errorMessages.savingNote);
  },
});
export const allTaskList = (state: RootState) => state.modelAssessmentReducer.allTaskList;

export const getCompletedTaskList = createSelector([allTaskList], (tasks) => {
  return tasks.filter(({ isCompleted }) => isCompleted === true);
});

export const selectMappedTaskList = createSelector(
  [allTaskList],
  tasks =>
    tasks.map(task => ({
      id: task.id,
      content: 'sourceText' in task ? task.sourceText : '',
      title: 'title' in task ? task.title : 'Survey',
      isCompleted: task.isCompleted,
      isBookmarked: task.isBookmarked,
    })),
);

export const getInCompletedTaskList = createSelector([selectMappedTaskList], (tasks) => {
  return tasks.filter(({ isCompleted }) => isCompleted === false);
});
export const getSelectedTaskById = createSelector(
  [allTaskList, (_, selectedTaskId?: number | null) => selectedTaskId],
  (tasks, selectedTaskId) => {
    return tasks.find((obj: RatingTask | TuaaTask) =>
      obj.id === selectedTaskId,
    );
  },
);

export const selectModelAssessmentReducerStatus = (state: RootState) => state.modelAssessmentReducer?.status;

export const {
  setSelectedTaskId,
  selectNextTaskId,
  submitAssignment,
} = modelAssessmentReducer.actions;
export default modelAssessmentReducer.reducer;
